<template>
  <div>

    <item-history
      :active.sync="itemHistory.active"
      :product="itemHistory.product"
      :type.sync="itemHistory.type"
      @updated-product="itemHistory.product = $event, getProducts()"
    ></item-history>



    <div class="box">
      <div class="flex justify-center">
        <div class="w-full md:w-1/2 text-center">
          <easy-date
            v-model="easyDateValue"
            :identifier.sync="easyIdentifier"
          ></easy-date>
          <div class="flex mt-4 items-center">
            <div class="h-10 px-4 py-2 rounded-l bg-red-500 text-white border border-r-0 border-theme-200 light:border-theme-600 focus:outline-none" @click="clear">
              <i class="fas fa-times"></i>
            </div>
            <flat-pickr
              v-model="dateRange.value"
              :config="dateRange.config.flatpickrConfig"
              class="form-control form-no-outline h-10 border-r-0 rounded-none"
              placeholder="Select date range"
              id="date-range"
              name="date_range"
            ></flat-pickr>
            <button
              type="button"
              class="h-10 px-5 flex items-center border border-purple-400 light:border-purple-600 rounded-r focus:outline-none hover:bg-purple-800 hover:border-purple-700"
              :class="dateRange.value ? 'bg-purple-500 text-white' : ''"
              @click="search"
            >
              <i class="fas fa-search"></i>
              <span class="ml-2">Search</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="box">
      <div class="w-full lg:w-2/3 mx-auto flex flex-wrap justify-between text-center">
        <div class="mt-4 leading-tight">
          <p class="font-bold">Total purchases:</p>
          <p class="text-2xl font-light text-green-500">{{ $formatMoney(total.purchases)}}</p>
        </div>
        <div class="mt-4 leading-tight">
          <p class="font-bold">Total sales:</p>
          <p class="text-2xl font-light text-red-500">{{ $formatMoney(total.sales)}}</p>
        </div>
        <div class="mt-4 leading-tight">
          <p class="font-bold">Stock value:</p>
          <p class="text-2xl font-light text-purple-200 light:text-purple-700">{{ $formatMoney(total.potential.purchase + total.potential.purchase_tax)}}</p>
        </div>
        <div class="mt-4 leading-tight">
          <p class="font-bold">Potential revenue:</p>
          <p class="text-2xl font-light text-purple-200 light:text-purple-700">{{ $formatMoney((total.potential.sale + total.potential.sale_tax) - (total.potential.purchase + total.potential.purchase_tax))}}</p>
        </div>
      </div>


      <p class="text-lg text-center my-12" v-if="!hasProductReports">No data to display</p>
      <div class="w-full overflow-x-auto mt-4" v-else>
        <table class="table table-striped">
          <thead>
            <tr>
              <th v-for="(head, i) in productReportHeaders" :key="'table-headings-' + i">
                <div class="flex items-center justify-between">
                  <p>{{ head.display }}</p>
                  <a class="float-right text-link " @click="setSortBy(head)" v-if="head.sortable">
                    <i class="fas" v-if="head.identifier === sortBy" :class="sortOrder === 'desc' ? 'fa-sort-down' : 'fa-sort-up'"></i>
                    <i class="fas fa-sort" v-else></i>
                  </a>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, i) in productReports" :key="i">
              <template v-if="i >= (pagination.currentPage * pagination.perPage) - pagination.perPage && i < pagination.currentPage * pagination.perPage">
                <td>
                  <p>#{{ product.id }} {{ product.name }} ({{ product.type | sentenceCase }})</p>
                  {{ product.code }}
                </td>
                <td class="text-green">{{ $formatMoney(product.total_sales) }}</td>
                <td class="text-red-500">{{ $formatMoney(product.total_purchases) }}</td>
                <td class="font-bold">{{ $numeral(product.stock).format('0,0') }}</td>
                <td class="text-green">{{ $formatMoney(product.sale_price) }}</td>
                <td class="text-green">{{ product.sale_tax }}%</td>
                <td class="text-green">{{ $formatMoney(product.potential_revenue) }}</td>
                <td class="text-red-500">{{ $formatMoney(product.purchase_price) }}</td>
                <td class="text-red-500">{{ product.purchase_tax }}%</td>
                <td>
                  <div class="flex">
                    <a
                      class="hover:text-orange-700 text-orange cursor-pointer p-2"
                      @click.stop="openItemHistory(product)"
                    ><i class="fas fa-history"></i></a>
                  </div>
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="w-full flex justify-center">
        <pagination-component
          class="mt-8"
          v-if="hasProductReports && pagination.totalRows / pagination.perPage > 1"
          v-model="pagination.currentPage"
          :total-rows="pagination.totalRows"
          :rows-per-page="pagination.perPage"
        />
      </div>

    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
const ItemHistory = resolve => require(['@/components/pages/invoices/ItemHistory'], resolve)
const EasyDate = resolve => require(['@/components/EasyDate'], resolve)
const PaginationComponent = resolve => require(['@/components/Pagination'], resolve)

export default {
  components: {
    ItemHistory,
    EasyDate,
    PaginationComponent,
  },
  data() {
    return {
      easyDateValue: '',
      easyIdentifier: '',
      dateRange: {
        value: '',
        config: {
          flatpickrConfig: {}
        }
      },
      productReports: [],
      productReportHeaders: [
        {identifier: 'product', display: 'Product', sortable: false},
        {identifier: 'total_sales', display: 'Total sales', sortable: true},
        {identifier: 'total_purchases', display: 'Total purchases', sortable: true},
        {identifier: 'stock', display: 'Stock', sortable: true},
        {identifier: 'sale_price', display: 'Sale Price', sortable: true},
        {identifier: 'sale_tax', display: 'Sale Tax', sortable: true},
        {identifier: 'potential_revenue', display: 'Potential revenue', sortable: true},
        {identifier: 'purchase_price', display: 'Purchase Price', sortable: true},
        {identifier: 'purchase_tax', display: 'Purchase Tax', sortable: true},
        {identifier: 'actions', display: 'Actions', sortable: false},
      ],
      sortBy: 'total_sales',
      sortOrder: 'desc',
      itemHistory: {
        active: false,
        product: {},
        type: '',
      },
      pagination: {
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
      }
    }
  },
  watch: {
    easyDateValue(dates) {
      if (!dates.from || !dates.to) {
        return
      }
      this.dateRange.value = dates.from.format(this.config.dateFormats.display) + ' to ' + dates.to.format(this.config.dateFormats.display)
      this.getProducts()
    }
  },
  computed: {
    total() {
      return {
        sales: this.productReports.map(e => parseFloat(e.total_sales)).reduce((a, b) => a + b, 0),
        purchases: this.productReports.map(e => parseFloat(e.total_purchases)).reduce((a, b) => a + b, 0),
        potential: {
          sale: this.productReports.map(e => e.potential.sale).reduce((a, b) => a + b, 0),
          sale_tax: this.productReports.map(e => e.potential.sale_tax).reduce((a, b) => a + b, 0),
          purchase: this.productReports.map(e => e.potential.purchase).reduce((a, b) => a + b, 0),
          purchase_tax: this.productReports.map(e => e.potential.purchase_tax).reduce((a, b) => a + b, 0),
        },
      }
    },
    hasProductReports() {
      return Object.entries(this.productReports).length > 0
    },
    ...mapGetters({
      config: 'config/config',
    })
  },
  created() {
    this.dateRange.config.flatpickrConfig = Object.assign({}, this.config.flatpickrConfig)
    this.dateRange.config.flatpickrConfig.mode = 'range'
    this.dateRange.config.flatpickrConfig.static = false
  },
  mounted() {
    this.easyIdentifier = 'current-month'
    this.getProducts()
  },
  methods: {
    clear() {
      this.dateRange.value = ''
      this.easyIdentifier = ''
      this.payments.totals = []
      this.payments.income = []
      this.payments.expense = []
    },
    search() {
      this.easyIdentifier = ''
      this.getProducts()
    },
    openItemHistory(product) {
      if (this.itemHistory.product.id === product.id) {
        this.closeItemHistory()
      }
      this.itemHistory.active = true
      this.itemHistory.product = product
      this.itemHistory.type = 'purchases'
    },
    closeItemHistory() {
      this.itemHistory.active = false
      this.itemHistory.product = {}
      this.itemHistory.type = ''
    },
    sortProductReports(a, b) {
      if (this.sortOrder === 'desc') {
        return b[this.sortBy] - a[this.sortBy]
      } else {
        return a[this.sortBy] - b[this.sortBy]
      }
    },
    setSortBy(heading) {
      if (!heading.sortable) {
        return
      }

      if (this.sortBy === heading.identifier) {
        this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc'
      } else {
        this.sortBy = heading.identifier
        this.sortOrder = 'desc'
      }
      this.productReports.sort(this.sortProductReports)
    },
    getProducts() {
      let dateRange = this.$getRangeDates(this.dateRange.value, this.config)

      axios.post('reports/products/totals', {
        from: dateRange.from,
        to: dateRange.to,
      })
        .then((response) => {
          this.productReports = response.data.map(e => {
            e.potential = {
              purchase: e.purchase_price * e.stock,
              sale: e.sale_price * e.stock,
            }
            e.potential.sale_tax = e.potential.sale * e.sale_tax / 100
            e.potential.purchase_tax = e.potential.purchase * e.purchase_tax / 100
            e.potential_revenue = (e.potential.sale + e.potential.sale_tax) - (e.potential.purchase + e.potential.purchase_tax)
            return e
          })
          this.pagination.totalRows = this.productReports.length
          this.productReports.sort(this.sortProductReports)
        })
    }
  }
}
</script>
